// src/context/VideoCallContext.jsx
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  Room,
  RoomEvent,
  Track,
  LocalAudioTrack,
  LocalVideoTrack,
  RemoteParticipant,
} from 'livekit-client';
import { getLiveKitToken } from '../utils/livekitToken'; // ✅ correct export name

const VideoCallContext = createContext();
export const useVideoCall = () => useContext(VideoCallContext);

export const VideoCallProvider = ({ children }) => {
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [localAudioEnabled, setLocalAudioEnabled] = useState(true);
  const [localVideoEnabled, setLocalVideoEnabled] = useState(true);
  const [remoteParticipants, setRemoteParticipants] = useState([]);
  
  const localVideoRef = useRef(null);
  const localAudioRef = useRef(null);

  // Request permissions before starting a call
  const requestPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      // Stop all tracks immediately – we only need to check permissions
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (err) {
      console.error('Permission error:', err);
      let message = 'Camera or microphone access denied.';
      if (err.name === 'NotAllowedError') message = 'Please allow camera and microphone access in your browser.';
      else if (err.name === 'NotFoundError') message = 'No camera or microphone found on this device.';
      setError(message);
      return false;
    }
  };

  // Join a call – creates or joins a LiveKit room
  const joinCall = async (roomName) => {
    if (!user) {
      setError('You must be logged in to start a call.');
      return false;
    }

    // Request permissions first
    const hasPermissions = await requestPermissions();
    if (!hasPermissions) return false;

    setIsConnecting(true);
    setError(null);

    try {
      // Get token from your backend / Firebase function
      const token = await getLiveKitToken(roomName, user.uid);
      if (!token) throw new Error('Failed to get LiveKit token');

      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      // Set up event listeners
      newRoom.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      newRoom.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      newRoom.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
      newRoom.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      newRoom.on(RoomEvent.Disconnected, () => {
        setRoom(null);
        setRemoteParticipants([]);
        if (localVideoRef.current) localVideoRef.current.srcObject = null;
        if (localAudioRef.current) localAudioRef.current.srcObject = null;
      });

      await newRoom.connect('wss://your-livekit-server.com', token);
      
      // Publish local tracks
      await newRoom.localParticipant.enableCameraAndMicrophone();
      
      setRoom(newRoom);
      setIsConnecting(false);
      return true;
    } catch (err) {
      console.error('Join call error:', err);
      setError(err.message || 'Unable to start call. Check your connection.');
      setIsConnecting(false);
      return false;
    }
  };

  // Leave the current call
  const leaveCall = async () => {
    if (room) {
      room.disconnect();
      setRoom(null);
      setRemoteParticipants([]);
    }
  };

  // Toggle audio/video
  const toggleAudio = async () => {
    if (!room) return;
    if (localAudioEnabled) {
      await room.localParticipant.setMicrophoneEnabled(false);
    } else {
      await room.localParticipant.setMicrophoneEnabled(true);
    }
    setLocalAudioEnabled(!localAudioEnabled);
  };

  const toggleVideo = async () => {
    if (!room) return;
    if (localVideoEnabled) {
      await room.localParticipant.setCameraEnabled(false);
    } else {
      await room.localParticipant.setCameraEnabled(true);
    }
    setLocalVideoEnabled(!localVideoEnabled);
  };

  // Event handlers
  const handleTrackSubscribed = (track, publication, participant) => {
    if (track.kind === Track.Kind.Video) {
      // Attach video track to a <video> element (handled in UI)
      const element = document.getElementById(`video-${participant.sid}`);
      if (element && track.attach) track.attach(element);
    } else if (track.kind === Track.Kind.Audio) {
      const element = document.getElementById(`audio-${participant.sid}`);
      if (element && track.attach) track.attach(element);
    }
  };

  const handleTrackUnsubscribed = (track, publication, participant) => {
    if (track.attachedElements) {
      track.detach();
    }
  };

  const handleParticipantConnected = (participant) => {
    setRemoteParticipants(prev => [...prev, participant]);
  };

  const handleParticipantDisconnected = (participant) => {
    setRemoteParticipants(prev => prev.filter(p => p.sid !== participant.sid));
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (room) {
        room.disconnect();
      }
    };
  }, [room]);

  const value = {
    room,
    isConnecting,
    error,
    localAudioEnabled,
    localVideoEnabled,
    remoteParticipants,
    joinCall,
    leaveCall,
    toggleAudio,
    toggleVideo,
    localVideoRef,
    localAudioRef,
  };

  return (
    <VideoCallContext.Provider value={value}>
      {children}
    </VideoCallContext.Provider>
  );
};